import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { generateKeyBetween } from "fractional-indexing";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  ITEM_PREFIX,
  type Group,
  type Item,
} from "../replicache/keys";
import {
  ATTACHMENT_PREFIX,
  TIME_ENTRY_PREFIX,
  type Attachment,
  type TimeEntry,
} from "../replicache/keys";
import { useReplicache, useSubscribe } from "../replicache/context";
import { uuidv7 } from "../replicache/uuid";
import { formatDmy } from "../format/date";
import { ModifierBar } from "./ModifierBar";
import { useSelection } from "./selection";
import { useUndo } from "./undo";
import { openShortcutHelp } from "./ShortcutHelp";
import {
  buildTree,
  computeHeaderStats,
  descendantsOf,
  findContext,
  findNode,
  flattenVisible,
  planChildOf,
  planDropAt,
  planIndent,
  planOutdent,
  planSiblingAfter,
  type DropPos,
  type TreeNode,
} from "./tree";

type DragState = {
  activeId: string;
  over: { id: string; pos: DropPos } | null;
};

const HIDE_COMPLETE_KEY = "johndo:hideComplete";

// ---------------------------------------------------------------------------
// Swipe broadcast.  When the user swipes a row that's part of a multi-
// selection, every other selected row mirrors the same translation +
// background tint live.  Trigger row keeps its own local state for the
// gesture lifecycle; followers subscribe to the broadcast.
// ---------------------------------------------------------------------------

type SwipeBroadcast = {
  triggerId: string | null;
  followerIds: ReadonlySet<string>;
  dx: number;
  redLevel: number;
};
let swipeBroadcast: SwipeBroadcast = {
  triggerId: null,
  followerIds: new Set(),
  dx: 0,
  redLevel: 0,
};
const swipeSubs = new Set<() => void>();
function publishSwipe(next: SwipeBroadcast) {
  swipeBroadcast = next;
  swipeSubs.forEach((fn) => fn());
}
function useFollowerSwipe(
  itemId: string,
): { dx: number; redLevel: number } | null {
  const [v, setV] = useState<{ dx: number; redLevel: number } | null>(null);
  useEffect(() => {
    const sync = () => {
      const s = swipeBroadcast;
      const isFollower = s.triggerId !== itemId && s.followerIds.has(itemId);
      if (isFollower) setV({ dx: s.dx, redLevel: s.redLevel });
      else setV((cur) => (cur === null ? cur : null));
    };
    swipeSubs.add(sync);
    sync();
    return () => {
      swipeSubs.delete(sync);
    };
  }, [itemId]);
  return v;
}

export function TreeView({
  group,
  focusMode = false,
  revealItemId = null,
  onRevealed,
}: {
  group: Group;
  /** When true, filter to starred items + their ancestor chain.  Hides
   *  the per-group add affordances and hide-complete checkbox, and lets
   *  the outer container stack multiple TreeViews vertically. */
  focusMode?: boolean;
  /** Deep-link request: when non-null and the item belongs to this tree,
   *  expand the ancestor chain and scroll the row into view, then call
   *  onRevealed() to clear it.  Originates from `?item=<id>` URL params
   *  (e.g. "View task →" links on share_activity notifications). */
  revealItemId?: string | null;
  onRevealed?: () => void;
}) {
  const rep = useReplicache();
  const undo = useUndo();

  const items = useSubscribe<Item[]>(
    rep,
    async (tx) => {
      const rows: Item[] = [];
      for await (const value of tx.scan({ prefix: ITEM_PREFIX }).values()) {
        const item = value as Item;
        if (item.groupId === group.id) rows.push(item);
      }
      return rows;
    },
    [],
    [group.id],
  );

  // "Hide complete" filter, sticky per device.  When on, completed items
  // (and their subtrees, because their parent disappears from buildTree's
  // root walk) are omitted from the rendered tree.
  const [hideComplete, setHideComplete] = useState<boolean>(() => {
    return localStorage.getItem(HIDE_COMPLETE_KEY) === "1";
  });
  useEffect(() => {
    localStorage.setItem(HIDE_COMPLETE_KEY, hideComplete ? "1" : "0");
  }, [hideComplete]);

  // In focus mode, restrict to starred items + their ancestor chain.
  // Ancestors are assumed to live in the same group (Items.groupId is
  // denormalised, and every UI-driven move keeps parent/child aligned).
  const focusVisibleIds: Set<string> | null = useMemo(() => {
    if (!focusMode) return null;
    const byId = new Map<string, Item>();
    for (const it of items) byId.set(it.id, it);
    const out = new Set<string>();
    for (const it of items) {
      if (!it.isFocused) continue;
      let cur: Item | undefined = it;
      const guard = new Set<string>();
      while (cur && !guard.has(cur.id)) {
        guard.add(cur.id);
        out.add(cur.id);
        cur = cur.parentId ? byId.get(cur.parentId) : undefined;
      }
    }
    return out;
  }, [focusMode, items]);

  const filteredItems = useMemo(() => {
    let xs = items;
    if (focusVisibleIds) xs = xs.filter((i) => focusVisibleIds.has(i.id));
    if (hideComplete) xs = xs.filter((i) => !i.isComplete);
    return xs;
  }, [items, hideComplete, focusVisibleIds]);

  // Local-only draft for a not-yet-named new task or header.  We hold the
  // draft entirely in React state until the user commits a non-empty
  // title, at which point we call rep.mutate.createItem.  Drafts never
  // reach Replicache/the server, so other devices can't see a stray
  // "(untitled)" row and any cancel is purely local — no propagated delete
  // can wipe out an edit a recipient made on the other side.
  type Draft = {
    id: string;
    parentId: string | null;
    sortKey: string;
    type: "task" | "header";
  };
  const [draft, setDraft] = useState<Draft | null>(null);

  // Fold the draft into the tree as a synthetic Item so it renders at the
  // correct depth + position.  Only `tree` / `visible` see this; the raw
  // `items` and `filteredItems` arrays stay clean for everything else.
  const itemsWithDraft = useMemo<Item[]>(() => {
    if (!draft) return filteredItems;
    const fake: Item = {
      id: draft.id,
      ownerId: "",
      groupId: group.id,
      parentId: draft.parentId,
      type: draft.type,
      title: "",
      notes: "",
      status: "not_started",
      priority: null,
      isFocused: false,
      startDate: null,
      dueDate: null,
      recurrence: null,
      reportingTo: null,
      sortKey: draft.sortKey,
      isComplete: false,
      createdAt: "",
      updatedAt: "",
      version: 0,
    };
    return [...filteredItems, fake];
  }, [filteredItems, draft, group.id]);

  const tree = useMemo(() => buildTree(itemsWithDraft), [itemsWithDraft]);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const visible = useMemo(() => flattenVisible(tree, collapsed), [tree, collapsed]);

  // ---- Deep-link reveal ----------------------------------------------------
  // Two-phase: (1) when a reveal request lands and the target lives in this
  // tree, uncollapse its ancestor chain; (2) once the row shows up in the
  // visible list, scroll it into view and consume the request.  Splitting
  // avoids racing React's commit — phase 2 naturally waits for the re-render
  // triggered by phase 1's setCollapsed.
  useEffect(() => {
    if (!revealItemId) return;
    const target = items.find((it) => it.id === revealItemId);
    if (!target) return; // not in this group's tree — another TreeView will handle it
    const byId = new Map(items.map((it) => [it.id, it]));
    const ancestors: string[] = [];
    let pid: string | null = target.parentId;
    const guard = new Set<string>();
    while (pid && !guard.has(pid)) {
      guard.add(pid);
      ancestors.push(pid);
      pid = byId.get(pid)?.parentId ?? null;
    }
    if (ancestors.length === 0) return;
    setCollapsed((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of ancestors) {
        if (next.has(id)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [revealItemId, items]);

  useEffect(() => {
    if (!revealItemId) return;
    if (!visible.some((n) => n.item.id === revealItemId)) return;
    const raf = requestAnimationFrame(() => {
      const el = document.querySelector(
        `[data-tree-row-id="${CSS.escape(revealItemId)}"]`,
      );
      if (el instanceof HTMLElement) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      onRevealed?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [revealItemId, visible, onRevealed]);

  // Selection is shared across views via context.  `open` is separate — the
  // detail pane opens only when the user clicks the explicit `…` button on
  // a row (see TreeRow).
  const {
    selectedId,
    select: setSelection,
    selectedIds,
    toggleInSelection,
    selectRange,
    consumeStickyModifiers,
    open: openDetail,
    clearSelection,
  } = useSelection();
  const [editingId, setEditingId] = useState<string | null>(null);
  // (Drag-select callbacks are declared just below `select`, which they
  // depend on for the anchor write.)
  const dragSelectActiveRef = useRef(false);

  // If the selected item is no longer visible (moved to another group,
  // deleted, or hidden by collapse), clear selection so the detail pane
  // doesn't render stale data.  We only clear if the selection points at an
  // item that *belongs to this group* — selections from elsewhere should be
  // left alone.
  useEffect(() => {
    if (!selectedId) return;
    const inThisGroup = items.some((i) => i.id === selectedId);
    if (!inThisGroup) return;
    if (!visible.some((n) => n.item.id === selectedId)) {
      setSelection(visible[0]?.item.id ?? null);
    }
  }, [visible, selectedId, items, setSelection]);

  const treeRef = useRef<HTMLDivElement>(null);
  const select = (id: string | null) => {
    setSelection(id);
    requestAnimationFrame(() => treeRef.current?.focus());
  };

  // --- Drag-select (long-press + drag to extend a contiguous range) -------
  // Long-press on a row enters this mode (with a brief vibration).  The
  // pressed row becomes the anchor; subsequent touchmove extends the
  // selection to whichever row the finger is currently over, found via
  // elementFromPoint + the `data-tree-row-id` attribute on each <li>.
  // Releasing the touch ends the mode.  Replaces the old long-press-to-
  // delete overlay (which was redundant with swipe-left-to-delete).
  const startDragSelect = useCallback(
    (id: string) => {
      dragSelectActiveRef.current = true;
      try {
        navigator.vibrate?.(20);
      } catch {
        /* not supported on this device */
      }
      select(id);
    },
    // `select` is stable enough for this purpose — it closes over
    // setSelection which is itself stable.  eslint-disable next line.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const extendDragSelect = useCallback(
    (clientX: number, clientY: number) => {
      const el = document.elementFromPoint(clientX, clientY);
      if (!el) return;
      const row = (el as Element).closest("[data-tree-row-id]");
      const overId = row?.getAttribute("data-tree-row-id");
      if (!overId) return;
      selectRange(
        overId,
        visible.map((n) => n.item.id),
      );
    },
    [selectRange, visible],
  );
  const endDragSelect = useCallback(() => {
    dragSelectActiveRef.current = false;
  }, []);

  // Swipe broadcast plumbing — the trigger row calls these to drive the
  // shared translate for every selected follower row.
  const beginSwipeBroadcast = useCallback(
    (triggerId: string) => {
      const followers = new Set<string>();
      if (selectedIds.has(triggerId) && selectedIds.size > 1) {
        for (const id of selectedIds) {
          if (id !== triggerId) followers.add(id);
        }
      }
      publishSwipe({
        triggerId,
        followerIds: followers,
        dx: 0,
        redLevel: 0,
      });
    },
    [selectedIds],
  );
  const updateSwipeBroadcast = useCallback((dx: number, redLevel: number) => {
    publishSwipe({ ...swipeBroadcast, dx, redLevel });
  }, []);
  const endSwipeBroadcast = useCallback(() => {
    publishSwipe({
      triggerId: null,
      followerIds: new Set(),
      dx: 0,
      redLevel: 0,
    });
  }, []);

  const toggleCollapse = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // -------------------------------------------------------------------------
  // Drag and drop
  // -------------------------------------------------------------------------

  const sensors = useSensors(
    // Desktop: small distance threshold so clicks on the handle don't
    // accidentally start a drag if the user's finger twitches.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    // Touch: long-press (no movement requirement), with generous tolerance so
    // normal finger jitter doesn't cancel activation.  The handle has
    // `touch-action: none` so the browser won't scroll while held.
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 12 } }),
  );

  const [drag, setDrag] = useState<DragState | null>(null);

  const forbidden = useMemo(() => {
    if (!drag) return new Set<string>();
    return descendantsOf(tree, drag.activeId);
  }, [drag, tree]);

  const onDragStart = (e: DragStartEvent) => {
    setDrag({ activeId: String(e.active.id), over: null });
    setEditingId(null);
  };

  const onDragMove = (e: DragMoveEvent) => {
    if (!drag) return;
    if (!e.over) {
      setDrag((d) => (d && d.over !== null ? { ...d, over: null } : d));
      return;
    }
    const overId = String(e.over.id);
    if (overId === drag.activeId || forbidden.has(overId)) {
      setDrag((d) => (d && d.over !== null ? { ...d, over: null } : d));
      return;
    }
    const overNode = visible.find((n) => n.item.id === overId);
    if (!overNode) return;
    // Pointer Y now: original click + cumulative delta.
    const activator = e.activatorEvent as PointerEvent;
    const pointerY = (activator.clientY ?? 0) + e.delta.y;
    const overRect = e.over.rect;
    const ratio = (pointerY - overRect.top) / overRect.height;
    const hasChildren = overNode.children.length > 0;
    let pos: DropPos;
    if (hasChildren) {
      pos = ratio < 0.3 ? "before" : ratio < 0.7 ? "inside" : "after";
    } else {
      pos = ratio < 0.5 ? "before" : "after";
    }
    setDrag((d) => {
      if (!d) return d;
      if (d.over?.id === overId && d.over.pos === pos) return d;
      return { ...d, over: { id: overId, pos } };
    });
  };

  const onDragEnd = (e: DragEndEvent) => {
    const current = drag;
    setDrag(null);
    if (!current?.over) return;
    if (!rep) return;
    const plan = planDropAt(tree, current.over.id, current.over.pos, collapsed);
    if (!plan) return;
    const prev = items.find((i) => i.id === current.activeId);
    void rep.mutate.moveItem({
      id: current.activeId,
      parentId: plan.parentId,
      sortKey: plan.sortKey,
    });
    if (prev) {
      undo.push({
        label: `Move "${prev.title || "task"}"`,
        undo: async () => {
          await rep.mutate.moveItem({
            id: prev.id,
            groupId: prev.groupId,
            parentId: prev.parentId,
            sortKey: prev.sortKey,
          });
        },
      });
    }
    select(current.activeId);
    // Touch the event so eslint doesn't complain about unused
    void e;
  };

  const onDragCancel = () => setDrag(null);

  // -------------------------------------------------------------------------
  // Mutator helpers (keyboard nav)
  // -------------------------------------------------------------------------

  const pushUndoCreate = useCallback(
    (id: string) => {
      undo.push({
        label: "Add task",
        undo: async () => {
          if (!rep) return;
          await rep.mutate.deleteItem({ id });
        },
      });
    },
    [undo, rep],
  );

  // Open a local draft for a new sibling-after / child-of / next-at-root.
  // No server write happens here — the row is purely local until
  // commitDraft fires with a non-empty title.  editingId is pinned to the
  // draft id so the outer keyboard handler stays dormant while the user
  // is typing in the draft input.
  const addSiblingAfter = useCallback(
    (afterId: string, type: "task" | "header" = "task") => {
      const plan = planSiblingAfter(tree, afterId);
      if (!plan) return;
      const id = uuidv7();
      setDraft({ id, parentId: plan.parentId, sortKey: plan.sortKey, type });
      setEditingId(id);
    },
    [tree],
  );

  const addChildOf = useCallback(
    (parentId: string, type: "task" | "header" = "task") => {
      const plan = planChildOf(tree, parentId);
      if (!plan) return;
      // Expand the parent so the draft is visible underneath it.
      setCollapsed((prev) => {
        if (!prev.has(parentId)) return prev;
        const next = new Set(prev);
        next.delete(parentId);
        return next;
      });
      const id = uuidv7();
      setDraft({ id, parentId: plan.parentId, sortKey: plan.sortKey, type });
      setEditingId(id);
    },
    [tree],
  );

  // "Add" button — mirrors ⌘↩: sibling-after the current selection (if any
  // belongs to this group); otherwise drop a draft at the end of the root.
  const addNext = useCallback(
    (type: "task" | "header" = "task") => {
      const inThisGroup =
        selectedId !== null && items.some((i) => i.id === selectedId);
      if (inThisGroup) {
        addSiblingAfter(selectedId!, type);
        return;
      }
      // Top-level: append after the last live root (ignoring any draft we
      // might already be folding into the tree — pick from `items`).
      const liveRoots = items.filter((i) => i.parentId === null);
      liveRoots.sort((a, b) =>
        a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0,
      );
      const lastRoot = liveRoots[liveRoots.length - 1] ?? null;
      const sortKey = generateKeyBetween(lastRoot?.sortKey ?? null, null);
      const id = uuidv7();
      setDraft({ id, parentId: null, sortKey, type });
      setEditingId(id);
    },
    [selectedId, items, addSiblingAfter],
  );

  // Commit a draft with the typed title (or empty → discard).  Returns
  // true if a real item was created.  `chainNext` opens another draft as
  // the next sibling on success — the "press Enter to start writing a new
  // task" rapid-entry workflow.
  const commitDraft = useCallback(
    async (title: string, chainNext: boolean): Promise<void> => {
      if (!draft) return;
      const trimmed = title.trim();
      if (!rep || trimmed === "") {
        setDraft(null);
        setEditingId(null);
        treeRef.current?.focus();
        return;
      }
      const committed = draft;
      await rep.mutate.createItem({
        id: committed.id,
        groupId: group.id,
        parentId: committed.parentId,
        type: committed.type,
        title: trimmed,
        sortKey: committed.sortKey,
      });
      pushUndoCreate(committed.id);
      if (chainNext) {
        // The committed item now sits at the same position the draft fake
        // occupied, so planSiblingAfter against the current tree gives
        // the right sort key for the next slot.
        const plan = planSiblingAfter(tree, committed.id);
        if (plan) {
          const nextId = uuidv7();
          setDraft({
            id: nextId,
            parentId: plan.parentId,
            sortKey: plan.sortKey,
            type: committed.type,
          });
          setEditingId(nextId);
          return;
        }
      }
      // Functional updates so a concurrent draft set by another handler
      // (e.g. clicking "+ Task" mid-typing fires our onBlur first, then
      // the click sets a new draft — without this guard, the trailing
      // setDraft(null) from this in-flight commit would wipe it.)
      setDraft((prev) => (prev?.id === committed.id ? null : prev));
      setEditingId((prev) => (prev === committed.id ? null : prev));
      select(committed.id);
    },
    [rep, draft, group.id, tree, pushUndoCreate],
  );

  const cancelDraft = useCallback(() => {
    setDraft(null);
    setEditingId(null);
    treeRef.current?.focus();
  }, []);

  const pushUndoMove = useCallback(
    (prev: Item) => {
      undo.push({
        label: `Move "${prev.title || "task"}"`,
        undo: async () => {
          if (!rep) return;
          await rep.mutate.moveItem({
            id: prev.id,
            groupId: prev.groupId,
            parentId: prev.parentId,
            sortKey: prev.sortKey,
          });
        },
      });
    },
    [undo, rep],
  );

  // (indent/outdent live inside the batch helper now — `planFreshAndMove`
  // below re-reads the tree before each plan, which avoids the stale-tree
  // cascade that hits when more than one sibling is processed in a row.)

  // Collect everything the recursive delete will sweep, so the undo can
  // re-insert it locally (optimistic) AND ask the server to clear the
  // matching deleted_at flags.
  const captureSubtree = useCallback(
    async (
      id: string,
    ): Promise<{
      root: Item;
      descendants: Item[];
      timeEntries: TimeEntry[];
      attachments: Attachment[];
    } | null> => {
      if (!rep) return null;
      const root = items.find((i) => i.id === id);
      if (!root) return null;
      const descSet = descendantsOf(tree, id); // includes id
      const descendants = items.filter((i) => i.id !== id && descSet.has(i.id));
      const timeEntries = await rep.query(async (tx) => {
        const list: TimeEntry[] = [];
        for await (const value of tx.scan({ prefix: TIME_ENTRY_PREFIX }).values()) {
          const e = value as TimeEntry;
          if (descSet.has(e.itemId)) list.push(e);
        }
        return list;
      });
      const attachments = await rep.query(async (tx) => {
        const list: Attachment[] = [];
        for await (const value of tx.scan({ prefix: ATTACHMENT_PREFIX }).values()) {
          const a = value as Attachment;
          if (descSet.has(a.itemId)) list.push(a);
        }
        return list;
      });
      return { root, descendants, timeEntries, attachments };
    },
    [rep, items, tree],
  );

  const deleteItem = useCallback(
    async (id: string) => {
      if (!rep) return;
      const ctx = findContext(tree, id);
      if (!ctx) return;
      const node = ctx.siblings[ctx.index]!;
      if (node.children.length > 0) {
        if (!confirm(`Delete "${node.item.title || "this item"}" and ${node.children.length} child item(s)?`)) return;
      }
      const snap = await captureSubtree(id);
      await rep.mutate.deleteItem({ id });
      if (snap) {
        undo.push({
          label: `Delete "${snap.root.title || "task"}"`,
          announce: true,
          undo: async () => {
            await rep.mutate.restoreItem({
              id: snap.root.id,
              item: snap.root,
              descendants: snap.descendants,
              timeEntries: snap.timeEntries,
              attachments: snap.attachments,
            });
          },
        });
      }
      const idx = visible.findIndex((n) => n.item.id === id);
      const next = visible[idx + 1] ?? visible[idx - 1] ?? null;
      select(next?.item.id ?? null);
    },
    [rep, tree, visible, captureSubtree, undo],
  );

  // ----- Batch dispatch -----------------------------------------------------
  // Each gesture/keyboard action takes a triggerId.  If that id is part of
  // the current multi-selection, the action runs against every selected
  // item; otherwise it's just the trigger.  Order matters for moves —
  // indent/outdent need to plan against the current tree, so we process
  // sequentially (awaiting each) in visible/DFS order so later items see
  // the state produced by earlier ones.

  // Any member of the multi-selection works as a trigger for the *Many
  // helpers (targetsFor expands it to the whole selection).
  const firstSelectedId = (): string | null =>
    selectedIds.size > 0 ? Array.from(selectedIds)[0]! : null;

  const targetsFor = useCallback(
    (triggerId: string): string[] => {
      if (selectedIds.has(triggerId) && selectedIds.size > 1) {
        const visibleIds = visible.map((n) => n.item.id);
        return Array.from(selectedIds).sort(
          (a, b) => visibleIds.indexOf(a) - visibleIds.indexOf(b),
        );
      }
      return [triggerId];
    },
    [selectedIds, visible],
  );

  // Batch indent/outdent.  Each iteration re-reads the live state from
  // Replicache so the plan reflects the previous mutation's effect —
  // otherwise consecutive sibling indents would cascade (item B planning
  // against A's pre-move position, ending up *inside* A instead of beside
  // it under A's new parent).
  const planFreshAndMove = useCallback(
    async (
      id: string,
      planner: (t: TreeNode[], id: string) => { parentId: string | null; sortKey: string } | null,
    ): Promise<void> => {
      if (!rep) return;
      const liveItems = await rep.query(async (tx) => {
        const rows: Item[] = [];
        for await (const value of tx.scan({ prefix: ITEM_PREFIX }).values()) {
          const it = value as Item;
          if (it.groupId === group.id) rows.push(it);
        }
        return rows;
      });
      const liveTree = buildTree(liveItems);
      const plan = planner(liveTree, id);
      if (!plan) return;
      const prev = liveItems.find((i) => i.id === id);
      await rep.mutate.moveItem({
        id,
        parentId: plan.parentId,
        sortKey: plan.sortKey,
      });
      if (prev) pushUndoMove(prev);
    },
    [rep, group.id, pushUndoMove],
  );

  const indentMany = useCallback(
    async (triggerId: string) => {
      for (const id of targetsFor(triggerId)) {
        await planFreshAndMove(id, planIndent);
      }
    },
    [targetsFor, planFreshAndMove],
  );
  const outdentMany = useCallback(
    async (triggerId: string) => {
      // Outdent bottom-up so we don't disturb each other's parents.
      const ids = targetsFor(triggerId).slice().reverse();
      for (const id of ids) {
        await planFreshAndMove(id, planOutdent);
      }
    },
    [targetsFor, planFreshAndMove],
  );
  const toggleFocusMany = useCallback(
    async (triggerId: string) => {
      if (!rep) return;
      const trig = items.find((i) => i.id === triggerId);
      if (!trig || trig.type !== "task") return;
      const wasFocused = trig.isFocused;
      const ids = targetsFor(triggerId).filter((id) => {
        const it = items.find((i) => i.id === id);
        return it?.type === "task";
      });
      const newValue = !wasFocused;
      for (const id of ids) {
        await rep.mutate.updateItem({ id, isFocused: newValue });
      }
      try {
        navigator.vibrate?.(newValue ? [30, 20, 30] : 20);
      } catch {
        /* ignore */
      }
      undo.push({
        label: newValue
          ? `Mark ${ids.length} task${ids.length === 1 ? "" : "s"} focused`
          : `Unmark ${ids.length} task${ids.length === 1 ? "" : "s"}`,
        undo: async () => {
          for (const id of ids) {
            await rep.mutate.updateItem({ id, isFocused: wasFocused });
          }
        },
      });
    },
    [rep, items, targetsFor, undo],
  );

  const toggleCompleteMany = useCallback(
    async (triggerId: string) => {
      if (!rep) return;
      // Use the trigger's current state as the master; every selected task
      // flips to the opposite of that state for a consistent feel.
      const trig = items.find((i) => i.id === triggerId);
      if (!trig || trig.type !== "task") return;
      const wasComplete = trig.isComplete;
      const ids = targetsFor(triggerId).filter((id) => {
        const it = items.find((i) => i.id === id);
        return it?.type === "task";
      });
      for (const id of ids) {
        if (wasComplete) await rep.mutate.uncompleteItem({ id });
        else await rep.mutate.completeItem({ id });
      }
      // Single undo flips them all back as a batch.
      undo.push({
        label: wasComplete
          ? `Uncomplete ${ids.length} task${ids.length === 1 ? "" : "s"}`
          : `Complete ${ids.length} task${ids.length === 1 ? "" : "s"}`,
        undo: async () => {
          for (const id of ids) {
            if (wasComplete) await rep.mutate.completeItem({ id });
            else await rep.mutate.uncompleteItem({ id });
          }
        },
      });
    },
    [rep, items, targetsFor, undo],
  );
  // Single-item archive: parallels deleteItem (single).  Archive is itself
  // recoverable via /archive so we skip the undo stack — the user would
  // restore from there, not from the in-tab undo button.
  const archiveItemAction = useCallback(
    async (id: string) => {
      if (!rep) return;
      const ctx = findContext(tree, id);
      if (!ctx) return;
      const node = ctx.siblings[ctx.index]!;
      const label = node.item.title || "this item";
      if (node.children.length > 0) {
        if (
          !confirm(
            `Archive "${label}" and ${node.children.length} child item(s)? You can restore from /archive.`,
          )
        )
          return;
      } else {
        if (!confirm(`Archive "${label}"? You can restore from /archive.`)) return;
      }
      await rep.mutate.archiveItem({ id });
      const idx = visible.findIndex((n) => n.item.id === id);
      const next = visible[idx + 1] ?? visible[idx - 1] ?? null;
      select(next?.item.id ?? null);
    },
    [rep, tree, visible, select],
  );

  const deleteMany = useCallback(
    async (triggerId: string) => {
      if (!rep) return;
      // Dedupe: if a selected item's ancestor is also selected, drop the
      // descendant — the cascade-delete on the ancestor covers it.
      const ids = targetsFor(triggerId);
      const idSet = new Set(ids);
      const isAncestorSelected = (id: string): boolean => {
        let cur = items.find((i) => i.id === id)?.parentId ?? null;
        while (cur !== null) {
          if (idSet.has(cur)) return true;
          cur = items.find((i) => i.id === cur)?.parentId ?? null;
        }
        return false;
      };
      const roots = ids.filter((id) => !isAncestorSelected(id));
      if (roots.length === 0) return;

      if (roots.length > 1) {
        if (!confirm(`Delete ${roots.length} tasks (with any sub-items)?`)) return;
      } else {
        // For a single deletion we let deleteItem do its own child confirm
        // (so the user knows about the cascade size).
        await deleteItem(roots[0]!);
        return;
      }

      const snaps: Awaited<ReturnType<typeof captureSubtree>>[] = [];
      for (const id of roots) {
        const snap = await captureSubtree(id);
        snaps.push(snap);
      }
      for (const id of roots) {
        await rep.mutate.deleteItem({ id });
      }
      // Single undo restores every snapshot.
      undo.push({
        label: `Delete ${roots.length} tasks`,
        announce: true,
        undo: async () => {
          for (const snap of snaps) {
            if (!snap) continue;
            await rep.mutate.restoreItem({
              id: snap.root.id,
              item: snap.root,
              descendants: snap.descendants,
              timeEntries: snap.timeEntries,
              attachments: snap.attachments,
            });
          }
        },
      });
      const idx = visible.findIndex((n) => n.item.id === triggerId);
      const next = visible[idx + 1] ?? visible[idx - 1] ?? null;
      select(next?.item.id ?? null);
    },
    [rep, items, targetsFor, visible, captureSubtree, undo, deleteItem],
  );

  // Bulk archive on multi-select.  Same root dedupe as deleteMany: if an
  // ancestor is in the selection, drop the descendant since the server
  // archive already cascades.  No undo — archive is itself the undo
  // (browse /archive and restore).
  const archiveMany = useCallback(
    async (triggerId: string) => {
      if (!rep) return;
      const ids = targetsFor(triggerId);
      const idSet = new Set(ids);
      const isAncestorSelected = (id: string): boolean => {
        let cur = items.find((i) => i.id === id)?.parentId ?? null;
        while (cur !== null) {
          if (idSet.has(cur)) return true;
          cur = items.find((i) => i.id === cur)?.parentId ?? null;
        }
        return false;
      };
      const roots = ids.filter((id) => !isAncestorSelected(id));
      if (roots.length === 0) return;
      if (roots.length === 1) {
        await archiveItemAction(roots[0]!);
        return;
      }
      if (
        !confirm(
          `Archive ${roots.length} tasks (with any sub-items)? You can restore from /archive.`,
        )
      )
        return;
      for (const id of roots) {
        await rep.mutate.archiveItem({ id });
      }
      const idx = visible.findIndex((n) => n.item.id === triggerId);
      const next = visible[idx + 1] ?? visible[idx - 1] ?? null;
      select(next?.item.id ?? null);
    },
    [rep, items, targetsFor, visible, archiveItemAction, select],
  );

  // "Archive all complete" — finds maximal subtrees where the root AND
  // every descendant is is_complete=true.  A complete parent with
  // incomplete children is NOT included, because archiving it would also
  // archive those incomplete children (their sync edges go through the
  // parent's subtree).  Walking from the top picks the largest covering
  // roots so a fully-done subtree archives in a single mutation.
  const completeSubtreeRoots = useMemo<string[]>(() => {
    const roots: string[] = [];
    const isFullyComplete = (n: TreeNode): boolean => {
      if (n.item.type !== "task") return false;
      if (!n.item.isComplete) return false;
      return n.children.every(isFullyComplete);
    };
    const walk = (nodes: TreeNode[]): void => {
      for (const n of nodes) {
        if (isFullyComplete(n)) roots.push(n.item.id);
        else if (n.children.length > 0) walk(n.children);
      }
    };
    walk(tree);
    return roots;
  }, [tree]);

  const archiveAllComplete = useCallback(async () => {
    if (!rep) return;
    if (completeSubtreeRoots.length === 0) return;
    if (
      !confirm(
        `Archive ${completeSubtreeRoots.length} completed ${
          completeSubtreeRoots.length === 1 ? "task" : "tasks"
        } in "${group.name}"? Any sub-items will be archived with the parent (only fully-complete subtrees are included). You can restore from /archive.`,
      )
    )
      return;
    for (const id of completeSubtreeRoots) {
      await rep.mutate.archiveItem({ id });
    }
  }, [rep, completeSubtreeRoots, group.name]);

  // -------------------------------------------------------------------------
  // Keyboard dispatch
  // -------------------------------------------------------------------------

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (editingId || drag) return;
    const node = selectedId ? visible.find((n) => n.item.id === selectedId) : null;
    const idx = node ? visible.indexOf(node) : -1;

    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (e.shiftKey && node) addChildOf(node.item.id);
      else if (node) addSiblingAfter(node.item.id);
      else if (visible.length === 0) {
        // No items yet: open a top-level draft.  Same code path as the
        // "+ Task" button so we never write an untitled row to the server.
        const id = uuidv7();
        setDraft({
          id,
          parentId: null,
          sortKey: generateKeyBetween(null, null),
          type: "task",
        });
        setEditingId(id);
      }
      return;
    }
    if (!node) return;

    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const next = visible[idx + 1];
        if (next) select(next.item.id);
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const prev = visible[idx - 1];
        if (prev) select(prev.item.id);
        break;
      }
      case "ArrowLeft": {
        e.preventDefault();
        if (node.children.length && !collapsed.has(node.item.id)) {
          toggleCollapse(node.item.id);
        } else {
          const ctx = findContext(tree, node.item.id);
          if (ctx?.parent) select(ctx.parent.item.id);
        }
        break;
      }
      case "ArrowRight": {
        e.preventDefault();
        if (node.children.length) {
          if (collapsed.has(node.item.id)) toggleCollapse(node.item.id);
          else select(node.children[0]!.item.id);
        }
        break;
      }
      case "Tab": {
        e.preventDefault();
        if (e.shiftKey) void outdentMany(node.item.id);
        else void indentMany(node.item.id);
        break;
      }
      case " ": {
        if (node.item.type === "task") {
          e.preventDefault();
          void toggleCompleteMany(node.item.id);
        }
        break;
      }
      case "Enter": {
        e.preventDefault();
        setEditingId(node.item.id);
        break;
      }
      case "Delete":
      case "Backspace": {
        e.preventDefault();
        void deleteMany(node.item.id);
        break;
      }
    }
  };

  // -------------------------------------------------------------------------
  // Stable per-row callbacks.  TreeRow is memoized; handing each row fresh
  // closures every render would defeat that, so rows get identity-stable
  // wrappers (cached per item id) that delegate to the current render's
  // implementations through latestRowImpl.  Selection/drag state changes
  // then re-render only the rows whose data props actually changed.
  // -------------------------------------------------------------------------

  const latestRowImpl = useRef<{
    onSelect: (id: string, modifiers: { shift: boolean; ctrl: boolean }) => void;
    onEndEdit: (id: string, title: string) => void;
    onCommitAndAddSibling: (id: string, title: string) => void;
    openDetail: (id: string) => void;
    startDragSelect: (id: string) => void;
    extendDragSelect: (clientX: number, clientY: number) => void;
    endDragSelect: () => void;
    beginSwipeBroadcast: (id: string) => void;
    updateSwipeBroadcast: (dx: number, redLevel: number) => void;
    endSwipeBroadcast: () => void;
    deleteMany: (id: string) => void;
    archiveMany: (id: string) => void;
    indentMany: (id: string) => void;
    outdentMany: (id: string) => void;
    toggleFocusMany: (id: string) => void;
    toggleCollapse: (id: string) => void;
    toggleCompleteMany: (id: string) => void;
    beginEdit: (id: string) => void;
  }>(null!);
  latestRowImpl.current = {
    onSelect: (id, modifiers) => {
      const sticky = consumeStickyModifiers();
      const shift = modifiers.shift || sticky.shift;
      const ctrl = modifiers.ctrl || sticky.ctrl;
      if (shift) {
        selectRange(
          id,
          visible.map((n) => n.item.id),
        );
      } else if (ctrl) {
        toggleInSelection(id);
      } else {
        select(id);
      }
    },
    onEndEdit: (id, title) => {
      const node = findNode(tree, id);
      setEditingId(null);
      // Return focus to the tree wrapper so Ctrl+Enter and the
      // arrow keys keep working without an extra mouse click —
      // bug 019e6121 ("Task Focus").
      treeRef.current?.focus();
      if (!node) return;
      // Discard brand-new tasks the user never typed into:
      // empty trimmed title AND no prior content means they
      // dismissed an empty row rather than authoring one
      // (feature 019e6094).
      if (title === "" && node.item.title === "") {
        void rep?.mutate.deleteItem({ id: node.item.id });
        return;
      }
      if (rep && title !== node.item.title) {
        const prevTitle = node.item.title;
        void rep.mutate.updateItem({ id, title });
        undo.push({
          label: "Edit title",
          undo: async () => {
            await rep.mutate.updateItem({ id, title: prevTitle });
          },
        });
      }
    },
    onCommitAndAddSibling: (id, title) => {
      const node = findNode(tree, id);
      setEditingId(null);
      if (node && rep && title !== node.item.title) {
        const prevTitle = node.item.title;
        void rep.mutate.updateItem({ id, title });
        undo.push({
          label: "Edit title",
          undo: async () => {
            await rep.mutate.updateItem({ id, title: prevTitle });
          },
        });
      }
      // addSiblingAfter opens a local draft row; the user types
      // into it directly without us writing an untitled task to
      // the server.
      addSiblingAfter(id);
    },
    openDetail,
    startDragSelect,
    extendDragSelect,
    endDragSelect,
    beginSwipeBroadcast,
    updateSwipeBroadcast,
    endSwipeBroadcast,
    deleteMany: (id) => void deleteMany(id),
    archiveMany: (id) => void archiveMany(id),
    indentMany: (id) => void indentMany(id),
    outdentMany: (id) => void outdentMany(id),
    toggleFocusMany: (id) => void toggleFocusMany(id),
    toggleCollapse,
    toggleCompleteMany: (id) => void toggleCompleteMany(id),
    beginEdit: (id) => setEditingId(id),
  };

  type RowCallbacks = {
    onSelect: (modifiers: { shift: boolean; ctrl: boolean }) => void;
    onOpenDetail: () => void;
    onLongPress: () => void;
    onDragSelectMove: (clientX: number, clientY: number) => void;
    onDragSelectEnd: () => void;
    onSwipeBegin: () => void;
    onSwipeUpdate: (dx: number, redLevel: number) => void;
    onSwipeEnd: () => void;
    onDelete: () => void;
    onArchive: () => void;
    onIndent: () => void;
    onOutdent: () => void;
    onFocus: () => void;
    onToggleCollapse: () => void;
    onToggleComplete: () => void;
    onBeginEdit: () => void;
    onEndEdit: (title: string) => void;
    onCommitAndAddSibling: (title: string) => void;
  };
  const rowCallbackCache = useRef(new Map<string, RowCallbacks>());
  const rowCallbacksFor = (id: string): RowCallbacks => {
    let cb = rowCallbackCache.current.get(id);
    if (!cb) {
      const impl = latestRowImpl;
      cb = {
        onSelect: (modifiers) => impl.current.onSelect(id, modifiers),
        onOpenDetail: () => impl.current.openDetail(id),
        onLongPress: () => impl.current.startDragSelect(id),
        onDragSelectMove: (x, y) => impl.current.extendDragSelect(x, y),
        onDragSelectEnd: () => impl.current.endDragSelect(),
        onSwipeBegin: () => impl.current.beginSwipeBroadcast(id),
        onSwipeUpdate: (dx, red) => impl.current.updateSwipeBroadcast(dx, red),
        onSwipeEnd: () => impl.current.endSwipeBroadcast(),
        onDelete: () => impl.current.deleteMany(id),
        onArchive: () => impl.current.archiveMany(id),
        onIndent: () => impl.current.indentMany(id),
        onOutdent: () => impl.current.outdentMany(id),
        onFocus: () => impl.current.toggleFocusMany(id),
        onToggleCollapse: () => impl.current.toggleCollapse(id),
        onToggleComplete: () => impl.current.toggleCompleteMany(id),
        onBeginEdit: () => impl.current.beginEdit(id),
        onEndEdit: (title) => impl.current.onEndEdit(id, title),
        onCommitAndAddSibling: (title) =>
          impl.current.onCommitAndAddSibling(id, title),
      };
      rowCallbackCache.current.set(id, cb);
    }
    return cb;
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (items.length === 0) {
    return (
      <EmptyState
        groupName={group.name}
        onAddFirst={async (title, type) => {
          if (!rep) return;
          const id = uuidv7();
          await rep.mutate.createItem({
            id,
            groupId: group.id,
            parentId: null,
            type,
            title,
            sortKey: generateKeyBetween(null, null),
          });
          pushUndoCreate(id);
          select(id);
        }}
      />
    );
  }

  const activeItem = drag
    ? items.find((i) => i.id === drag.activeId) ?? null
    : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <div
        ref={treeRef}
        tabIndex={0}
        onKeyDown={onKeyDown}
        className={
          focusMode
            ? "flex flex-col gap-1 border-b border-slate-200 dark:border-slate-700 px-6 py-4 focus:outline-none last:border-b-0"
            : "flex flex-1 flex-col gap-1 p-6 focus:outline-none"
        }
      >
        <header className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <h2
            className={
              focusMode
                ? "text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
                : "text-xl font-semibold tracking-tight"
            }
          >
            {group.name}
          </h2>
          {!focusMode && (
            <div className="flex flex-wrap items-center gap-2">
              <ModifierBar />
              <label className="flex items-center gap-1 text-[11px] text-slate-600 dark:text-slate-300">
                <input
                  type="checkbox"
                  checked={hideComplete}
                  onChange={(e) => setHideComplete(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-slate-300 dark:border-slate-600"
                />
                Hide complete
              </label>
              {completeSubtreeRoots.length > 0 && (
                <button
                  type="button"
                  onClick={() => void archiveAllComplete()}
                  title="Archive every fully-completed task subtree in this group"
                  className="rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  🗄️ Archive {completeSubtreeRoots.length} complete
                </button>
              )}
              <button
                type="button"
                onClick={() => void addNext("task")}
                className="rounded-md bg-slate-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
              >
                + Task
              </button>
              <button
                type="button"
                onClick={() => void addNext("header")}
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                + Header
              </button>
              <button
                type="button"
                onClick={openShortcutHelp}
                title="Keyboard shortcuts (?)"
                aria-label="Keyboard shortcuts"
                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-500 hover:bg-slate-50 hover:text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              >
                ⌨ ?
              </button>
            </div>
          )}
        </header>

        <ul role="tree" className="space-y-0.5">
          {visible.map((node) => {
            if (draft && node.item.id === draft.id) {
              return (
                <DraftRow
                  key={node.item.id}
                  depth={node.depth}
                  type={draft.type}
                  onCommit={(title) => void commitDraft(title, false)}
                  onCommitAndNew={(title) => void commitDraft(title, true)}
                  onCancel={cancelDraft}
                />
              );
            }
            return (
            <TreeRow
              key={node.item.id}
              node={node}
              selected={node.item.id === selectedId}
              editing={node.item.id === editingId}
              collapsed={collapsed.has(node.item.id)}
              dragging={drag?.activeId === node.item.id}
              dropIndicator={
                drag?.over?.id === node.item.id ? drag.over.pos : null
              }
              forbidden={forbidden.has(node.item.id) && drag !== null && drag.activeId !== node.item.id}
              selectedInBatch={selectedIds.has(node.item.id)}
              dragSelectActiveRef={dragSelectActiveRef}
              {...rowCallbacksFor(node.item.id)}
            />
            );
          })}
          {!focusMode && (
            <li>
              <button
                type="button"
                onClick={() => void addNext("task")}
                className="mt-1 flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-sm text-slate-400 hover:bg-slate-50 hover:text-slate-700 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              >
                <span aria-hidden className="text-base leading-none">+</span>
                <span>Add task</span>
              </button>
            </li>
          )}
        </ul>
      </div>
      {selectedIds.size > 1 && (
        <div
          role="toolbar"
          aria-label="Bulk actions"
          className="safe-inset fixed bottom-4 left-1/2 z-40 flex -translate-x-1/2 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5 shadow-lg dark:border-slate-700 dark:bg-slate-900"
        >
          <span className="px-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">
            {selectedIds.size} selected
          </span>
          <BulkButton
            label="✓ Complete"
            onClick={() => void toggleCompleteMany(firstSelectedId()!)}
          />
          <BulkButton
            label="Archive"
            onClick={() => void archiveMany(firstSelectedId()!)}
          />
          <BulkButton
            label="Delete"
            destructive
            onClick={() => void deleteMany(firstSelectedId()!)}
          />
          <button
            type="button"
            onClick={clearSelection}
            aria-label="Clear selection"
            className="ml-1 rounded-md px-1.5 py-1 text-xs text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            ✕
          </button>
        </div>
      )}
      <DragOverlay dropAnimation={null}>
        {activeItem ? <GhostRow item={activeItem} /> : null}
      </DragOverlay>
    </DndContext>
  );
}

// ---------------------------------------------------------------------------
// Tree row — draggable + droppable.  Memoized: the parent hands every row
// identity-stable callbacks (see rowCallbacksFor), so a selection or drag
// state change re-renders only the rows whose data props changed.
// ---------------------------------------------------------------------------

const TreeRow = memo(function TreeRow({
  node,
  selected,
  selectedInBatch,
  editing,
  collapsed,
  dragging,
  dropIndicator,
  forbidden,
  onSelect,
  onOpenDetail,
  onLongPress,
  onDragSelectMove,
  onDragSelectEnd,
  dragSelectActiveRef,
  onSwipeBegin,
  onSwipeUpdate,
  onSwipeEnd,
  onDelete,
  onArchive,
  onIndent,
  onOutdent,
  onFocus,
  onToggleCollapse,
  onToggleComplete,
  onBeginEdit,
  onEndEdit,
  onCommitAndAddSibling,
}: {
  node: TreeNode;
  selected: boolean;
  editing: boolean;
  collapsed: boolean;
  dragging: boolean;
  dropIndicator: DropPos | null;
  forbidden: boolean;
  selectedInBatch: boolean;
  onSelect: (modifiers: { shift: boolean; ctrl: boolean }) => void;
  onOpenDetail: () => void;
  onLongPress: () => void;
  onDragSelectMove: (clientX: number, clientY: number) => void;
  onDragSelectEnd: () => void;
  /** Stable ref the parent uses to flip drag-select on/off; the row reads
   *  it inside its native touch listener to know whether to extend the
   *  selection range instead of swiping. */
  dragSelectActiveRef: { current: boolean };
  /** Lifecycle callbacks for the swipe broadcast — let the parent publish
   *  the live dx/redLevel so other selected rows can mirror the translate. */
  onSwipeBegin: () => void;
  onSwipeUpdate: (dx: number, redLevel: number) => void;
  onSwipeEnd: () => void;
  onDelete: () => void;
  onArchive: () => void;
  onIndent: () => void;
  onOutdent: () => void;
  onFocus: () => void;
  onToggleCollapse: () => void;
  onToggleComplete: () => void;
  onBeginEdit: () => void;
  onEndEdit: (title: string) => void;
  /** Plain-Enter "rapid entry" path: commit current title and immediately
   *  open a new sibling for editing.  Distinct from onEndEdit so the parent
   *  can suppress its own focus-restoration (addSiblingAfter will move
   *  edit focus into the new row's input instead). */
  onCommitAndAddSibling: (title: string) => void;
}) {
  const { item, depth, children } = node;
  const indentPx = depth * 20;
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(item.title);
  // Enter triggers blur() to commit; the blur handler reads this ref to
  // decide between plain commit ("end") and commit-then-new-sibling
  // ("end-and-new").  Cleared back to "end" after each consumption so a
  // later focus-loss (e.g. click away) doesn't accidentally spawn a row.
  const blurActionRef = useRef<"end" | "end-and-new">("end");

  // useDraggable's setNodeRef goes on the li (its rect is used for collision
  // detection), but the activation listeners/attributes go on a small drag
  // handle inside the row.  That way touch anywhere except the handle still
  // scrolls the page normally, and accidental drags from clicks on the
  // checkbox / disclosure / title are impossible.
  const draggable = useDraggable({ id: item.id, disabled: editing });
  const droppable = useDroppable({ id: item.id, disabled: dragging || forbidden });

  useEffect(() => {
    if (editing) {
      setDraft(item.title);
      requestAnimationFrame(() => inputRef.current?.select());
    }
  }, [editing, item.title]);

  // Combined refs (kept for compatibility; the real combined ref lives next
  // to the native touch listener so we can attach handlers to the DOM node).
  const setRef = useCallback(
    (el: HTMLLIElement | null) => {
      draggable.setNodeRef(el);
      droppable.setNodeRef(el);
    },
    [draggable, droppable],
  );
  // Suppress unused-warning during incremental refactor.
  void setRef;

  const indicatorAbove = dropIndicator === "before";
  const indicatorBelow = dropIndicator === "after";
  const indicatorInside = dropIndicator === "inside";

  // --- Long-press + swipe detection ----------------------------------------
  //
  // The row has to disambiguate three pointer gestures on the same surface:
  //   1. Tap (no movement) → onClick → onSelect.
  //   2. Long-press (500 ms hold, <6 px movement) → onLongPress.
  //   3. Horizontal swipe (>6 px x, primarily horizontal) → indent/outdent.
  //
  // Vertical scroll is preserved because the row uses `touch-action: pan-y`
  // (set inline on render); the browser claims vertical pointer streams
  // for scrolling before our handlers see them.
  //
  // We use the first significant movement to "lock in" the gesture:
  //   - if dy dominates, we drop swipe tracking (let it scroll).
  //   - if dx dominates, we start the swipe and cancel the long-press.
  const pressTimer = useRef<number | null>(null);
  const pressOrigin = useRef<{ x: number; y: number } | null>(null);
  const swipeStarted = useRef(false);
  const suppressClick = useRef(false);
  // State drives the live transform; the ref mirrors it synchronously so the
  // pointerup handler sees the latest value (React state from pointermove
  // hasn't necessarily re-rendered before pointerup fires).
  const [swipeDx, setSwipeDx] = useState(0);
  const swipeDxRef = useRef(0);

  const SWIPE_COMMIT_PX = 60; // distance to count as a commit-worthy swipe
  const SWIPE_MAX_PX = 110; // visual cap on the live preview

  const clearPress = () => {
    if (pressTimer.current !== null) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
    pressOrigin.current = null;
  };
  const stopRedAnim = () => {
    if (redRaf.current !== null) {
      cancelAnimationFrame(redRaf.current);
      redRaf.current = null;
    }
    holdRedStart.current = null;
    setRedLevel(0);
  };
  const stopGoldAnim = () => {
    if (goldRaf.current !== null) {
      cancelAnimationFrame(goldRaf.current);
      goldRaf.current = null;
    }
    holdGoldStart.current = null;
    setGoldLevel(0);
  };
  const resetSwipe = () => {
    swipeStarted.current = false;
    swipeDxRef.current = 0;
    setSwipeDx(0);
    stopRedAnim();
    stopGoldAnim();
  };
  const onRowPointerDown = (e: React.PointerEvent) => {
    // Mouse / pen only — touch goes through the native listener below.
    if (e.pointerType === "touch") return;
    if (e.button !== undefined && e.button !== 0) return;
    if (editing) return;
    pressOrigin.current = { x: e.clientX, y: e.clientY };
    pressTimer.current = window.setTimeout(() => {
      pressTimer.current = null;
      onLongPress();
    }, 500);
  };
  const onRowPointerMove = (e: React.PointerEvent) => {
    if (e.pointerType === "touch") return;
    if (!pressOrigin.current) return;
    const dx = e.clientX - pressOrigin.current.x;
    const dy = e.clientY - pressOrigin.current.y;
    if (Math.abs(dx) > 6 || Math.abs(dy) > 6) clearPress();
  };
  const onRowPointerUp = (e: React.PointerEvent) => {
    if (e.pointerType === "touch") return;
    clearPress();
  };

  // Native touch handling.  React's delegated touchmove is passive — we
  // can't preventDefault from a synthetic handler, so the browser scrolls
  // away from us as soon as the gesture diverges from `pan-y`.  Attaching
  // a non-passive listener directly on the row's element gives us the
  // control we need to hold the swipe.
  const rowElRef = useRef<HTMLLIElement | null>(null);
  const touchOrigin = useRef<{ x: number; y: number } | null>(null);
  const touchTimer = useRef<number | null>(null);

  // Mirror the latest indent/outdent/long-press callbacks in refs.  The
  // touch listener is attached once per row mount and the callbacks change
  // every parent re-render (because they close over `tree`); without these
  // refs the listener fires a STALE callback against a STALE tree, which
  // is what made consecutive swipes "skip a generation" or no-op.
  const onIndentRef = useRef(onIndent);
  const onOutdentRef = useRef(onOutdent);
  const onLongPressRef = useRef(onLongPress);
  const onDeleteRef = useRef(onDelete);
  const onFocusRef = useRef(onFocus);
  const onDragSelectMoveRef = useRef(onDragSelectMove);
  const onDragSelectEndRef = useRef(onDragSelectEnd);
  const onSwipeBeginRef = useRef(onSwipeBegin);
  const onSwipeUpdateRef = useRef(onSwipeUpdate);
  const onSwipeEndRef = useRef(onSwipeEnd);
  onIndentRef.current = onIndent;
  onOutdentRef.current = onOutdent;
  onLongPressRef.current = onLongPress;
  onDeleteRef.current = onDelete;
  onFocusRef.current = onFocus;
  onDragSelectMoveRef.current = onDragSelectMove;
  onDragSelectEndRef.current = onDragSelectEnd;
  onSwipeBeginRef.current = onSwipeBegin;
  onSwipeUpdateRef.current = onSwipeUpdate;
  onSwipeEndRef.current = onSwipeEnd;

  // Hold-to-delete on outdent swipe (left).  Total 0.25 s from entering
  // the amber zone to deletion: 100 ms in amber, 150 ms blending to red.
  // Releasing in either phase still fires the normal outdent; reaching
  // fully red pops a confirm dialog before actually deleting.
  const RED_DELAY_MS = 100;
  const RED_FADE_MS = 150;
  const holdRedStart = useRef<number | null>(null);
  const redRaf = useRef<number | null>(null);
  const [redLevel, setRedLevel] = useState(0);

  // Mirror set for the swipe-right-hold "mark focused" gesture.  Same
  // timings: short swipe-right past threshold = indent, hold past
  // threshold for ~0.25 s = fade to gold + toggle is_focused.
  const GOLD_DELAY_MS = 100;
  const GOLD_FADE_MS = 150;
  const holdGoldStart = useRef<number | null>(null);
  const goldRaf = useRef<number | null>(null);
  const [goldLevel, setGoldLevel] = useState(0);

  const setCombinedRef = useCallback(
    (el: HTMLLIElement | null) => {
      draggable.setNodeRef(el);
      droppable.setNodeRef(el);
      rowElRef.current = el;
    },
    [draggable, droppable],
  );

  useEffect(() => {
    const el = rowElRef.current;
    if (!el) return;
    if (editing) return;

    let started = false;
    let armed = false;

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0]!;
      touchOrigin.current = { x: t.clientX, y: t.clientY };
      started = false;
      armed = true;
      touchTimer.current = window.setTimeout(() => {
        touchTimer.current = null;
        if (!started) onLongPressRef.current();
      }, 500);
    };
    const cancelTimer = () => {
      if (touchTimer.current !== null) {
        window.clearTimeout(touchTimer.current);
        touchTimer.current = null;
      }
    };
    const onMove = (e: TouchEvent) => {
      if (!armed || !touchOrigin.current) return;
      const t = e.touches[0];
      if (!t) return;

      // Drag-select takes priority once the long-press timer has fired.  We
      // own the pointer in this mode — every move extends the range to
      // whatever row the finger is currently over, and the browser's
      // normal scroll is suppressed.
      if (dragSelectActiveRef.current) {
        if (e.cancelable) e.preventDefault();
        onDragSelectMoveRef.current(t.clientX, t.clientY);
        return;
      }

      const dx = t.clientX - touchOrigin.current.x;
      const dy = t.clientY - touchOrigin.current.y;
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);

      if (!started) {
        // Vertical-dominant → let the page scroll.  Disarm ourselves.
        if (ady > 8 && ady > adx) {
          armed = false;
          cancelTimer();
          return;
        }
        // Horizontal-dominant → claim the gesture.
        if (adx > 6 && adx > ady) {
          started = true;
          swipeStarted.current = true;
          cancelTimer();
          onSwipeBeginRef.current();
        }
      }

      if (started) {
        // Stop the browser from scrolling / interpreting this as anything
        // else.  Only works because this listener is non-passive.
        if (e.cancelable) e.preventDefault();
        const clamped =
          dx > 0 ? Math.min(dx, SWIPE_MAX_PX) : Math.max(dx, -SWIPE_MAX_PX);
        swipeDxRef.current = clamped;
        setSwipeDx(clamped);
        onSwipeUpdateRef.current(clamped, 0);

        // Hold-past-outdent-threshold → start fading toward delete-red.
        // Leave the zone (swipe back right or up) → cancel the fade.
        const inOutdentZone = clamped <= -SWIPE_COMMIT_PX;
        if (inOutdentZone) {
          if (holdRedStart.current === null) startHoldRedTick();
        } else {
          stopRedAnim();
        }
        // Mirror for the indent zone → fade toward focus-gold.
        const inIndentZone = clamped >= SWIPE_COMMIT_PX;
        if (inIndentZone) {
          if (holdGoldStart.current === null) startHoldGoldTick();
        } else {
          stopGoldAnim();
        }
      } else if (adx > 6 || ady > 6) {
        cancelTimer();
      }
    };

    // Drives the amber→red gradient + auto-fires delete when fully red.
    // Lives inside the effect so it shares closure state with onMove.
    const startHoldRedTick = () => {
      holdRedStart.current = performance.now();
      const tick = (now: number) => {
        if (holdRedStart.current === null) return;
        const elapsed = now - holdRedStart.current;
        if (elapsed < RED_DELAY_MS) {
          setRedLevel(0);
        } else {
          const t = Math.min(1, (elapsed - RED_DELAY_MS) / RED_FADE_MS);
          setRedLevel(t);
          onSwipeUpdateRef.current(swipeDxRef.current, t);
          if (t >= 1) {
            // Buzz, then confirm, then fire.  navigator.vibrate is a no-op
            // on iOS Safari — safe to call unconditionally.
            try {
              navigator.vibrate?.([60, 30, 80]);
            } catch {
              /* ignore */
            }
            // Suppress the post-touchend click that would otherwise re-
            // select the (now deleted) row.
            suppressClick.current = true;
            resetSwipe();
            onSwipeEndRef.current();
            armed = false;
            started = false;
            touchOrigin.current = null;
            const fire = onDeleteRef.current;
            const title = node.item.title || "this task";
            if (window.confirm(`Delete "${title}"?`)) fire();
            return;
          }
        }
        redRaf.current = requestAnimationFrame(tick);
      };
      redRaf.current = requestAnimationFrame(tick);
    };

    // Gold-fade tick — mirror of the red one but for the indent direction.
    // At the end of the fade, fire onFocus (toggle is_focused) and reset
    // the swipe state.  Single-vibrate to confirm the action.
    const startHoldGoldTick = () => {
      holdGoldStart.current = performance.now();
      const goldTick = (now: number) => {
        if (holdGoldStart.current === null) return;
        const elapsed = now - holdGoldStart.current;
        if (elapsed < GOLD_DELAY_MS) {
          setGoldLevel(0);
        } else {
          const t = Math.min(1, (elapsed - GOLD_DELAY_MS) / GOLD_FADE_MS);
          setGoldLevel(t);
          onSwipeUpdateRef.current(swipeDxRef.current, 0);
          if (t >= 1) {
            suppressClick.current = true;
            resetSwipe();
            onSwipeEndRef.current();
            armed = false;
            started = false;
            touchOrigin.current = null;
            onFocusRef.current();
            return;
          }
        }
        goldRaf.current = requestAnimationFrame(goldTick);
      };
      goldRaf.current = requestAnimationFrame(goldTick);
    };

    const finish = (commit: boolean) => {
      cancelTimer();
      if (dragSelectActiveRef.current) {
        // The touch that started drag-select just ended → leave the mode.
        // Swallow the click that would otherwise fire on touchend so the
        // anchor row doesn't get re-selected on its own (which would
        // collapse the just-built range).
        onDragSelectEndRef.current();
        suppressClick.current = true;
      } else if (started) {
        if (commit) {
          const dx = swipeDxRef.current;
          if (dx >= SWIPE_COMMIT_PX) onIndentRef.current();
          else if (dx <= -SWIPE_COMMIT_PX) onOutdentRef.current();
        }
        suppressClick.current = true;
        resetSwipe();
        onSwipeEndRef.current();
      }
      armed = false;
      started = false;
      touchOrigin.current = null;
    };
    const onEnd = () => finish(true);
    const onCancel = () => finish(true);

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd, { passive: true });
    el.addEventListener("touchcancel", onCancel, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onCancel);
    };
    // onIndent/onOutdent/onLongPress are stable per render but listed for
    // correctness; React handles re-binding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);
  const onRowClick = (e: React.MouseEvent) => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    onSelect({ shift: e.shiftKey, ctrl: e.ctrlKey || e.metaKey });
  };

  // Mirror the trigger row's swipe live for other selected rows.
  const follower = useFollowerSwipe(item.id);
  const effDx = follower ? follower.dx : swipeDx;
  const effRed = follower ? follower.redLevel : redLevel;

  // Tint background hint based on swipe direction:
  //   - swipe-right past threshold: blue (indent) interpolating to gold
  //     (focus) as the user holds, driven by goldLevel.
  //   - swipe-left past threshold: amber (outdent) interpolating to red
  //     (delete) as the user holds, driven by effRed (redLevel).
  //   - either direction, below threshold: slate (still pulling).
  const swipeCommitting = Math.abs(effDx) >= SWIPE_COMMIT_PX;
  let swipeBgClass = "";
  let swipeBgStyle: string | undefined;
  if (effDx !== 0) {
    if (effDx > 0 && swipeCommitting) {
      // blue-100 (#dbeafe) → yellow-400 (#facc15)
      const r = Math.round(219 + (250 - 219) * goldLevel);
      const g = Math.round(234 + (204 - 234) * goldLevel);
      const b = Math.round(254 + (21 - 254) * goldLevel);
      swipeBgStyle = `rgb(${r}, ${g}, ${b})`;
    } else if (effDx < 0 && swipeCommitting) {
      // amber-100 (#fef3c7) → rose-500 (#f43f5e)
      const r = Math.round(254 + (244 - 254) * effRed);
      const g = Math.round(243 + (63 - 243) * effRed);
      const b = Math.round(199 + (94 - 199) * effRed);
      swipeBgStyle = `rgb(${r}, ${g}, ${b})`;
    } else {
      swipeBgClass = "bg-slate-100 dark:bg-slate-800";
    }
  }

  return (
    <li
      ref={setCombinedRef}
      data-tree-row-id={item.id}
      role="treeitem"
      aria-selected={selected}
      aria-expanded={children.length ? !collapsed : undefined}
      onClick={onRowClick}
      onPointerDown={onRowPointerDown}
      onPointerMove={onRowPointerMove}
      onPointerUp={onRowPointerUp}
      onPointerCancel={onRowPointerUp}
      style={{
        paddingLeft: `${indentPx + 4}px`,
        opacity: dragging ? 0.3 : 1,
        // Cap horizontal touch panning at the browser level so vertical
        // scroll continues to work while swipe-h triggers our handlers.
        // user-select / touch-callout are disabled site-wide in index.css.
        touchAction: "pan-y",
        // 2px solid colored borders for drop indicator; collapse when no
        // indicator so the layout doesn't shift.
        borderTop: indicatorAbove ? "2px solid rgb(37, 99, 235)" : "2px solid transparent",
        borderBottom: indicatorBelow ? "2px solid rgb(37, 99, 235)" : "2px solid transparent",
        transform: effDx ? `translateX(${effDx}px)` : undefined,
        transition: effDx === 0 ? "transform 120ms ease-out" : undefined,
        backgroundColor: swipeBgStyle,
      }}
      className={`group flex items-center gap-1 rounded-md pr-2 ${
        indicatorInside
          ? "bg-blue-100 ring-1 ring-inset ring-blue-300 dark:bg-blue-950 dark:ring-blue-800"
          : swipeBgStyle
            ? "" /* inline backgroundColor wins */
            : swipeBgClass
              ? swipeBgClass
              : selectedInBatch
                ? selected
                  ? "bg-slate-200 dark:bg-slate-700"
                  : "bg-slate-100 dark:bg-slate-800"
                : "hover:bg-slate-50 dark:hover:bg-slate-800"
      }`}
    >
      {/* Drag handle — the only surface that initiates a drag.  Touch-action
          none stops the browser from scrolling while held. */}
      <button
        type="button"
        ref={(el) => {
          if (el) el.style.touchAction = "none";
        }}
        {...draggable.attributes}
        {...draggable.listeners}
        onClick={(e) => e.stopPropagation()}
        aria-label="Drag to reorder"
        tabIndex={-1}
        className="flex h-6 w-4 cursor-grab items-center justify-center text-slate-300 hover:text-slate-600 active:cursor-grabbing dark:text-slate-600 dark:hover:text-slate-300"
      >
        <svg
          viewBox="0 0 8 14"
          aria-hidden
          className="h-3.5 w-2 fill-current"
        >
          <circle cx="2" cy="2" r="1" />
          <circle cx="2" cy="7" r="1" />
          <circle cx="2" cy="12" r="1" />
          <circle cx="6" cy="2" r="1" />
          <circle cx="6" cy="7" r="1" />
          <circle cx="6" cy="12" r="1" />
        </svg>
      </button>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleCollapse();
        }}
        onPointerDown={(e) => e.stopPropagation()}
        className={`flex h-5 w-5 items-center justify-center rounded text-xs text-slate-400 hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-200 ${
          children.length ? "" : "invisible"
        }`}
        tabIndex={-1}
        aria-label={collapsed ? "Expand" : "Collapse"}
      >
        {collapsed ? "▸" : "▾"}
      </button>

      {/* Marker + checkbox column for tasks; matched-width spacer for
          headers.  The negative left-margins on marker and checkbox
          collapse the gap-1 spacing between chevron→marker and
          marker→checkbox, outdenting the task title by ~8px.  The header
          spacer compensates so that sibling task and header titles
          start at the same x-offset from the chevron — without this
          symmetry the two row types looked badly mis-aligned in
          mixed-content groups. */}
      {item.type === "task" ? (
        <>
          <span className="relative -ml-1">
            {item.isFocused && (
              <span
                aria-label="Currently focused"
                title="Currently focused"
                className="pointer-events-none absolute right-full top-1/2 mr-0.5 -translate-y-1/2 select-none text-amber-400 leading-none"
              >
                ★
              </span>
            )}
            <PriorityMarker priority={item.priority} />
          </span>
          <input
            type="checkbox"
            checked={item.isComplete}
            onClick={(e) => e.stopPropagation()}
            // Prevent pointerdown bubbling so checkbox clicks don't start a drag.
            onPointerDown={(e) => e.stopPropagation()}
            onChange={onToggleComplete}
            className="-ml-1 h-4 w-4 rounded border-slate-300 dark:border-slate-600"
            tabIndex={-1}
          />
        </>
      ) : (
        <span aria-hidden className="block w-4 shrink-0" />
      )}

      {editing ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            const action = blurActionRef.current;
            blurActionRef.current = "end";
            const trimmed = draft.trim();
            if (action === "end-and-new" && trimmed !== "") {
              onCommitAndAddSibling(trimmed);
            } else {
              // Empty-trim falls back to item.title so existing tasks
              // aren't wiped by a stray blur; brand-new tasks (item.title
              // === "") still hit onEndEdit with "" so the parent can
              // delete them.
              onEndEdit(trimmed || item.title);
            }
          }}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
              // Rapid entry: commit + open a new sibling.  An empty draft
              // routes to plain end-edit so the empty row is discarded
              // rather than spawning a chain of empties.
              e.preventDefault();
              blurActionRef.current =
                draft.trim() === "" ? "end" : "end-and-new";
              (e.target as HTMLInputElement).blur();
            } else if (e.key === "Enter") {
              // Modified Enter (Cmd/Ctrl/Shift) — preserve the old
              // "blur to commit, then let outer keymap handle" behavior.
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            } else if (e.key === "Escape") {
              e.preventDefault();
              setDraft(item.title);
              (e.target as HTMLInputElement).blur();
            }
            e.stopPropagation();
          }}
          className="flex-1 rounded border border-slate-300 bg-white px-1 py-0.5 text-sm dark:border-slate-600 dark:bg-slate-800"
        />
      ) : (
        <span
          onDoubleClick={(e) => {
            e.stopPropagation();
            onBeginEdit();
          }}
          className={`flex-1 truncate select-none text-sm ${
            item.type === "header"
              ? "font-semibold tracking-tight text-slate-800 dark:text-slate-100"
              : item.isComplete
                ? "text-slate-400 line-through dark:text-slate-500"
                : "text-slate-800 dark:text-slate-100"
          }`}
        >
          {item.title || <span className="italic text-slate-400 dark:text-slate-500">(untitled)</span>}
          {item.recurrence && (
            <span
              aria-label="Repeats"
              title={`Repeats ${item.recurrence}`}
              className="ml-1 select-none text-xs text-sky-500"
            >
              ↻
            </span>
          )}
        </span>
      )}

      {/* Roll-up stats on headers: number of descendant tasks, how many
          high-priority, and how many due in the next two days. */}
      {item.type === "header" && <HeaderStatsChip node={node} />}

      {/* Compact date columns — start and due.  Hidden on the narrowest
          viewports so the row doesn't crowd; visible from `sm:` up. */}
      <DateColumns
        startDate={item.startDate}
        dueDate={item.dueDate}
        overdue={
          !item.isComplete &&
          item.dueDate !== null &&
          item.dueDate < todayISO()
        }
      />

      {/* Right-side action cluster: open-detail …, plus archive + delete
          shortcuts on desktop only (md+).  Mobile keeps swipe-left for
          delete and the … button to open the pane.  All three follow the
          same visible-on-hover/select pattern so the row looks clean at
          rest. */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenDetail();
        }}
        onPointerDown={(e) => e.stopPropagation()}
        aria-label="Open detail pane"
        title="Open details"
        tabIndex={-1}
        className={`ml-1 rounded px-1 text-base leading-none text-slate-400 hover:bg-slate-200 hover:text-slate-700 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-200 ${
          selected ? "" : "invisible group-hover:visible"
        }`}
      >
        …
      </button>
      {item.type === "task" && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onArchive();
            }}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label="Archive"
            title="Archive (restorable from /archive)"
            tabIndex={-1}
            className={`ml-0.5 hidden rounded px-1 text-sm leading-none text-slate-400 hover:bg-slate-200 hover:text-slate-700 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-200 md:inline-flex ${
              selected ? "" : "md:invisible md:group-hover:visible"
            }`}
          >
            🗄️
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label="Delete"
            title="Delete"
            tabIndex={-1}
            className={`ml-0.5 hidden rounded px-1 text-sm leading-none text-slate-400 hover:bg-rose-100 hover:text-rose-700 dark:text-slate-500 dark:hover:bg-rose-950 dark:hover:text-rose-300 md:inline-flex ${
              selected ? "" : "md:invisible md:group-hover:visible"
            }`}
          >
            🗑️
          </button>
        </>
      )}
    </li>
  );
});

// ---------------------------------------------------------------------------
// Compact start/due date display for tree rows.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Header roll-up chip — shows descendant task count + high-priority count +
// due-soon count next to each header's title.  Hidden when the header has
// no descendant tasks, and individual sub-pills hide when their count is
// zero so the chip doesn't shout "0 high · 0 due soon" on a fresh header.
// ---------------------------------------------------------------------------

function HeaderStatsChip({ node }: { node: TreeNode }) {
  const stats = useMemo(() => computeHeaderStats(node), [node]);
  if (stats.tasks === 0) return null;
  return (
    <span className="ml-2 shrink-0 select-none whitespace-nowrap text-[11px] text-slate-500 dark:text-slate-400">
      <span className="text-slate-600 dark:text-slate-300">
        {stats.tasks} task{stats.tasks === 1 ? "" : "s"}
      </span>
      {stats.highPriority > 0 && (
        <>
          <span className="mx-1 text-slate-300 dark:text-slate-600">·</span>
          <span className="font-medium text-rose-600 dark:text-rose-400">
            {stats.highPriority} high
          </span>
        </>
      )}
      {stats.dueSoon > 0 && (
        <>
          <span className="mx-1 text-slate-300 dark:text-slate-600">·</span>
          <span className="font-medium text-amber-700 dark:text-amber-300">
            {stats.dueSoon} due soon
          </span>
        </>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Priority marker — small coloured bar to the left of a task's checkbox.
// Placeholder (transparent) when no priority is set so checkbox alignment
// across the column stays steady.  High priority gets the gentle pulse
// (see `.animate-priority-pulse` in index.css).
// ---------------------------------------------------------------------------

function PriorityMarker({
  priority,
}: {
  priority: "low" | "medium" | "high" | null;
}) {
  const colour =
    priority === "high"
      ? "bg-rose-500"
      : priority === "medium"
        ? "bg-amber-500"
        : priority === "low"
          ? "bg-sky-500"
          : "bg-transparent";
  const pulse = priority === "high" ? "animate-priority-pulse" : "";
  return (
    <span
      aria-label={priority ? `${priority} priority` : "no priority"}
      title={priority ? `${priority} priority` : ""}
      className={`inline-block h-3.5 w-1 rounded-sm ${colour} ${pulse}`}
    />
  );
}

function DateColumns({
  startDate,
  dueDate,
  overdue,
}: {
  startDate: string | null;
  dueDate: string | null;
  overdue: boolean;
}) {
  if (!startDate && !dueDate) return null;
  return (
    <span
      className={`hidden shrink-0 select-none whitespace-nowrap text-[11px] sm:inline ${
        overdue ? "font-medium text-rose-600 dark:text-rose-400" : "text-slate-500 dark:text-slate-400"
      }`}
      title={`${startDate ?? "—"} → ${dueDate ?? "—"}`}
    >
      {fmtShortDate(startDate)}
      {startDate && dueDate && (
        <span aria-hidden className="mx-1 text-slate-300 dark:text-slate-600">
          →
        </span>
      )}
      {fmtShortDate(dueDate)}
    </span>
  );
}

function fmtShortDate(iso: string | null): string {
  // Project-wide "d-mmm-yy" via the shared helper — keeps the tree row
  // dates consistent with the detail pane, admin panel, and share-link
  // expiry display.  The helper handles the YYYY-MM-DD / full-ISO-datetime
  // duality without timezone drift.
  return formatDmy(iso);
}

function todayISO(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Minimal ghost row that follows the cursor during a drag.
function GhostRow({ item }: { item: Item }) {
  return (
    <div className="flex max-w-md items-center gap-2 rounded-md bg-white px-2 py-1 text-sm shadow-lg ring-1 ring-slate-300 dark:bg-slate-900 dark:ring-slate-600">
      {item.type === "task" ? (
        <input
          type="checkbox"
          readOnly
          checked={item.isComplete}
          className="h-4 w-4 rounded border-slate-300 dark:border-slate-600"
        />
      ) : null}
      <span
        className={
          item.type === "header"
            ? "font-semibold tracking-tight text-slate-800 dark:text-slate-100"
            : "text-slate-800 dark:text-slate-100"
        }
      >
        {item.title || "(untitled)"}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Draft row — purely-local placeholder for a not-yet-saved new task or
// header.  The row never reaches Replicache or the server: only when the
// user commits with a non-empty title do we call createItem.  This is
// how we keep other devices from briefly seeing an "(untitled)" row that
// the drafter may abandon (and worse, having a cancel propagate as a
// delete that nukes edits other devices made in the meantime).
// ---------------------------------------------------------------------------

function DraftRow({
  depth,
  type,
  onCommit,
  onCommitAndNew,
  onCancel,
}: {
  depth: number;
  type: "task" | "header";
  onCommit: (title: string) => void;
  onCommitAndNew: (title: string) => void;
  onCancel: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState("");
  // blur is the single commit/discard funnel — Enter and Esc set this
  // ref then call .blur() so we have one consistent path that runs
  // exactly once per draft lifecycle.  Without the ref, a click-away
  // after Enter could double-fire onCommit.
  const blurActionRef = useRef<"commit" | "commit-and-new" | "cancel">(
    "commit",
  );

  useEffect(() => {
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  return (
    <li
      className="flex items-center gap-1 rounded-md px-2 py-1"
      style={{ paddingLeft: `${depth * 20 + 8}px` }}
    >
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={type === "header" ? "New header…" : "New task…"}
        onBlur={() => {
          const action = blurActionRef.current;
          blurActionRef.current = "commit";
          const trimmed = draft.trim();
          if (action === "cancel") return;
          if (action === "commit-and-new" && trimmed !== "") {
            onCommitAndNew(trimmed);
          } else {
            onCommit(trimmed);
          }
        }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
            // Plain Enter: rapid entry.  Empty draft falls through to
            // plain commit (which is itself a discard, since trimmed is "").
            e.preventDefault();
            blurActionRef.current =
              draft.trim() === "" ? "commit" : "commit-and-new";
            (e.target as HTMLInputElement).blur();
          } else if (e.key === "Enter") {
            // Modified Enter: commit-only.
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          } else if (e.key === "Escape") {
            e.preventDefault();
            blurActionRef.current = "cancel";
            onCancel();
          }
          e.stopPropagation();
        }}
        className={`flex-1 rounded border border-slate-300 bg-white px-1 py-0.5 text-sm dark:border-slate-600 dark:bg-slate-800 ${
          type === "header" ? "font-semibold tracking-tight text-slate-800 dark:text-slate-100" : ""
        }`}
      />
    </li>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState({
  groupName,
  onAddFirst,
}: {
  groupName: string;
  onAddFirst: (title: string, type: "task" | "header") => Promise<void>;
}) {
  // Lets the first row be either a task or a header — bug 019e3f09
  // (the original empty state always created a task, which forced
  // header-first organisers to immediately convert it).
  const [title, setTitle] = useState("");
  const [type, setType] = useState<"task" | "header">("task");
  const submit = (kind: "task" | "header") => {
    const trimmed = title.trim();
    if (!trimmed) return;
    void onAddFirst(trimmed, kind);
    setTitle("");
    // Bias the next add toward whichever kind they just used — useful
    // for "header, then a few tasks under it" rhythms.
    setType(kind);
  };
  return (
    <div className="flex flex-1 flex-col gap-4 p-6">
      <h2 className="text-xl font-semibold tracking-tight">{groupName}</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit(type);
        }}
        className="flex flex-wrap items-center gap-2"
      >
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={type === "header" ? "First header…" : "First task…"}
          className="min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 dark:border-slate-600 dark:bg-slate-800"
        />
        <button
          type="button"
          onClick={() => submit("task")}
          disabled={!title.trim()}
          aria-pressed={type === "task"}
          className={`rounded-md px-3 py-1 text-sm font-medium disabled:opacity-50 ${
            type === "task"
              ? "bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
              : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          }`}
        >
          + Task
        </button>
        <button
          type="button"
          onClick={() => submit("header")}
          disabled={!title.trim()}
          aria-pressed={type === "header"}
          className={`rounded-md px-3 py-1 text-sm font-medium disabled:opacity-50 ${
            type === "header"
              ? "bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
              : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          }`}
        >
          + Header
        </button>
      </form>
      <p className="text-xs text-slate-400 dark:text-slate-500">
        Use Tab to indent · Cmd-Enter for a sibling · Cmd-Shift-Enter for a child · Drag rows to reorder
      </p>
    </div>
  );
}

function BulkButton({
  label,
  onClick,
  destructive,
}: {
  label: string;
  onClick: () => void;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-2 py-1 text-xs font-medium ${
        destructive
          ? "text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
          : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
      }`}
    >
      {label}
    </button>
  );
}
